import React, {useState, useEffect, useRef} from 'react'

import {
    Button,
    Text,
    Flex,
    useMultiStyleConfig,
    useStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useHistory} from 'react-router-dom'
import PropTypes from 'prop-types'
import {Menu, MenuButton, MenuItem, MenuList} from '@chakra-ui/react'
import {ChevronDown, CheckIcon} from '../../../components/icons'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'
import {useGoogleTagManager} from '../../../custom-api/hooks/use-google-tag-manager'
import {basic_gtm_event} from '../../../custom-api/layers/default'
import {remCalc} from '../../../theme/_utils'
import {FormattedMessage} from 'react-intl'

const SortByButton = ({
    selectedSortingOptionLabel,
    sortUrls,
    productSearchResult,
    onSelect = noop,
}) => {
    const styles = useMultiStyleConfig('Filters')
    const menuItemsStyle = useStyleConfig('SortByAccordionItem', {variant: 'sortByButton'})
    const history = useHistory()
    const [value, setValue] = useState(selectedSortingOptionLabel?.label)
    const menuItemRefs = useRef([])
    const menuButtonRef = useRef(null)
    const [focusedIndex, setFocusedIndex] = useState(0)
    const {addLayer} = useGoogleTagManager()

    useEffect(() => {
        setValue(selectedSortingOptionLabel?.label)
    }, [selectedSortingOptionLabel?.label])

    useEffect(() => {
        // Focus first item when menu opens
        if (menuItemRefs.current[0]) {
            setTimeout(() => {
                menuItemRefs.current[0]?.focus()
            }, 0)
        }
    }, [])

    const handleChange = (newValue) => {
        const selectedOption = productSearchResult?.sortingOptions.find(
            (option) => option.label === newValue,
        )
        const selectedUrl = sortUrls[productSearchResult?.sortingOptions.indexOf(selectedOption)]

        addLayer(
            basic_gtm_event.bind(this, {
                event: 'ga_event',
                event_name: 'engagement',
                eventCategory: 'Sort',
                eventAction: 'filter_apply',
                eventLabel: selectedOption?.label || selectedOption?.id || '',
            }),
        )

        setValue(newValue)
        history.push(selectedUrl)
        onSelect()
    }

    const handleMenuListKeyDown = (e, onClose) => {
        if (e.key === 'Tab') {
            e.preventDefault()
            e.stopPropagation()

            const currentIndex = focusedIndex
            const isLastItem = currentIndex === productSearchResult?.sortingOptions?.length - 1
            const isFirstItem = currentIndex === 0

            if (e.shiftKey) {
                if (isFirstItem) {
                    onClose()
                    setTimeout(() => {
                        menuButtonRef.current?.focus()
                    }, 0)
                } else {
                    const prevIdx = currentIndex - 1
                    setFocusedIndex(prevIdx)
                    menuItemRefs.current[prevIdx]?.focus()
                }
            } else {
                if (isLastItem) {
                    onClose()
                } else {
                    const nextIdx = currentIndex + 1
                    setFocusedIndex(nextIdx)
                    menuItemRefs.current[nextIdx]?.focus()
                }
            }
        }
    }

    const handleKeyDown = (e) => {
        if (e.key === 'Tab') {
            e.preventDefault()
            e.stopPropagation()
        }
    }

    const handleMenuItemFocus = (idx) => {
        setFocusedIndex(idx)
    }

    return (
        <Menu>
            {({isOpen, onClose}) => (
                <>
                    <MenuButton
                        as={Button}
                        variant="ghost-rounded"
                        size="base"
                        ref={menuButtonRef}
                        rightIcon={
                            <Flex
                                width={remCalc(16)}
                                height={remCalc(16)}
                                padding={remCalc(2)}
                                justifyContent="center"
                                alignItems="center"
                            >
                                <ChevronDown
                                    width={remCalc(12)}
                                    height={remCalc(12)}
                                    color="foreground.neutral.default"
                                    style={{
                                        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                    }}
                                />
                            </Flex>
                        }
                        background="background.neutral.default"
                    >
                        <Text
                            color="foreground.neutral.default"
                            textStyle="body-small"
                            fontWeight="medium"
                        >
                            {value ? (
                                value
                            ) : (
                                <FormattedMessage
                                    id="product_list.button.order_by"
                                    defaultMessage="Order By"
                                />
                            )}
                        </Text>
                    </MenuButton>
                    <MenuList
                        {...styles.sortByFilterMenuList}
                        onKeyDown={(e) => handleMenuListKeyDown(e, onClose)}
                    >
                        <Flex {...styles?.sortByMenuWrapper}>
                            <Flex
                                {...menuItemsStyle?.menuContainer}
                                p={remCalc(4)}
                                gap={remCalc(2)}
                            >
                                {productSearchResult?.sortingOptions?.map((option, idx) => (
                                    <MenuItem
                                        key={idx}
                                        ref={(el) => (menuItemRefs.current[idx] = el)}
                                        onKeyDown={(e) => handleKeyDown(e, idx, onClose)}
                                        onFocus={() => handleMenuItemFocus(idx)}
                                        onClick={() => handleChange(option.label)}
                                        tabIndex={0}
                                        p={remCalc(6)}
                                        {...menuItemsStyle.menuItem}
                                        // _focusVisible={{boxShadow: 'outline', bg: 'none'}}
                                        _hover={{
                                            bg: 'background.base.under',
                                            borderRadius: 'large',
                                        }}
                                        // _focus={{bg: 'none'}}
                                    >
                                        <Flex
                                            width="100%"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            minHeight={remCalc(18)}
                                        >
                                            <Text
                                                {...menuItemsStyle?.text}
                                                textStyle="body-small"
                                                fontWeight="regular"
                                                color="foreground.neutral.default"
                                            >
                                                {option.label}
                                            </Text>
                                            {value === option.label && (
                                                <CheckIcon
                                                    width={remCalc(12)}
                                                    height={remCalc(12)}
                                                    color="foreground.neutral.default"
                                                />
                                            )}
                                        </Flex>
                                    </MenuItem>
                                ))}
                            </Flex>
                        </Flex>
                    </MenuList>
                </>
            )}
        </Menu>
    )
}

SortByButton.propTypes = {
    selectedSortingOptionLabel: PropTypes.shape({
        label: PropTypes.string,
    }),
    sortUrls: PropTypes.arrayOf(PropTypes.string).isRequired,
    productSearchResult: PropTypes.shape({
        sortingOptions: PropTypes.arrayOf(
            PropTypes.shape({
                label: PropTypes.string,
            }),
        ),
    }),
    onSelect: PropTypes.func,
}

export default SortByButton
